import { FlosIcon, SectionHeader, SelectableBoxField } from '@flos/react-ui';
import { FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';
import { useResourceBundle } from '../contexts/ResourceContext';
import { useAppSelector } from '../hooks/useRedux';
import { navigationActions } from '../store/navigation.slice';
import { AreaType, CategoryType } from '../store/navigation.type';
import CategoryComment from './CategoryComment';
import ClaimTopic from './ClaimTopic';
import InsuranceTopic from './InsuranceTopic';
import SectionClosed from './SectionClosed';
import WaysOfBooking from './WaysOfBooking';

interface NextSectionProps {
  area: AreaType | undefined;
  category: CategoryType | undefined;
}

const categoryUi: {
  [key in CategoryType]: { icon: string; label: string };
} = {
  insurance: {
    icon: 'umbrella',
    label: 'label.category.insurance',
  },
  injury: {
    icon: 'car-collision',
    label: 'label.category.claim',
  },
  others: {
    icon: 'chat',
    label: 'label.category.others',
  },
};

const Categories: FunctionComponent = () => {
  const dispatch = useDispatch();
  const resource = useResourceBundle();

  const selectedArea = useAppSelector((state) => state.navigation.area);
  const selectedCategory = useAppSelector((state) => state.navigation.category);

  const select = (name: string) => {
    dispatch(navigationActions.selectCategory({ category: name as CategoryType }));
  };

  return (
    <div className="u-Space-up--lg">
      <SectionHeader title={resource['label.category.header']} />
      <SelectableBoxField
        options={Object.entries(categoryUi).map(([category, ui]) => ({
          label: (
            <>
              <FlosIcon shape={ui.icon} size="small" />
              {resource[ui.label]}
            </>
          ),
          value: category,
        }))}
        onChangeValue={select}
        value={selectedCategory ?? ''}
      />
      {<NextSection area={selectedArea} category={selectedCategory} />}
    </div>
  );
};

export default Categories;

const NextSection: FunctionComponent<NextSectionProps> = ({ area, category }) => {
  const activeModules = useAppSelector((state) => state.settings.activeModules);

  switch (category) {
    case 'insurance':
      return <InsuranceTopic />;
    case 'injury':
      if (!!activeModules.claims) {
        return area === 'private' ? <ClaimTopic /> : <WaysOfBooking />;
      } else {
        return <SectionClosed path="shared/content-blocks/generel/selvbetjening/bestil-et-opkald/lukning-af-skader" />;
      }
    case 'others':
      return (
        <>
          <CategoryComment />
          <WaysOfBooking autoScroll={false} />
        </>
      );
    default:
      return null;
  }
};
